/**
 * Redux slice para gerenciamento do idioma da aplicação.
 *
 * Este módulo define o estado e as ações relacionadas ao idioma da interface,
 * utilizando Redux Toolkit para simplificar o gerenciamento de estado.
 *
 * @module languageSlice
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

/**
 * Obtém o idioma salvo no localStorage ou usa inglês como padrão.
 *
 * @constant {string|null} i18nextLng
 */
const i18nextLng = localStorage.getItem('i18nextLng');

/**
 * Estado inicial do slice de idioma.
 *
 * @constant {Object} initialState
 * @property {string} value - Código do idioma atual ('en', 'ptbr', 'es', etc.)
 */
const initialState = {
  value: i18nextLng || 'en',
};

/**
 * Slice Redux para gerenciamento do idioma.
 *
 * Define o estado e os reducers para manipulação do idioma da aplicação.
 *
 * @type {import('@reduxjs/toolkit').Slice}
 */
const languageSlice = createSlice({
  name: 'language',
  initialState,
  reducers: {
    /**
     * Reducer para alterar o idioma atual.
     *
     * @function changeLanguage
     * @param {Object} state - Estado atual do Redux
     * @param {Object} action - Ação Redux com o novo idioma
     * @param {string} action.payload - Código do novo idioma ('en', 'ptbr', 'es', etc.)
     */
    changeLanguage: (state, action) => {
      state.value = action.payload;
    },
  },
});

/**
 * Thunk assíncrono de exemplo (não utilizado para o idioma).
 *
 * Este é um exemplo de thunk assíncrono que pode ser usado para
 * operações assíncronas relacionadas ao Redux.
 *
 * @function incrementAsync
 * @param {number} amount - Valor a ser retornado após o delay
 * @returns {Promise<number>} Promise que resolve com o valor após 1 segundo
 */
export const incrementAsync = createAsyncThunk(
  'counter/incrementAsync',
  async (amount) => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return amount;
  }
);

// Exporta a ação de mudança de idioma
export const { changeLanguage } = languageSlice.actions;

// Exporta o reducer para ser usado na store
export default languageSlice.reducer;
